import { useEffect, useState } from "react"
import { useSelector } from "react-redux"
import { widgetCartHoldSelector, widgetCartItemsSelector, widgetCartPayingSelector } from "store/experiences"
import { CART_HOLD_PROMPT_SECONDS, CART_HOLD_STATUSES } from "../constants"

// The cart's hold as the API keeps it (one timer for the whole cart), counted down every second.
// `isExpired` once the time is up, or when the API holds none of the cart's items (a cart saved
// before holds, or one the API no longer knows): Proceed to Checkout then holds them again. `shouldAsk`
// in the last minute ("Still shopping?", which offers the extension while `extensionsLeft`). Not
// active while a payment runs, nor once Pay has made the booking or order: that holds the places
// then, whatever the cart's timer says. `isReservationOver`: nothing may be paid until "Check
// availability & continue" reserves the cart again — the held items' time is up (on this screen's
// clock, with no call: the API stopped holding then), or an item reads expired (released, or its
// order expired) or isn't held at all (the API no longer knows the cart). Items the check found gone
// (lost) are changed or removed instead.
const useCartHold = () => {
  const hold = useSelector(widgetCartHoldSelector)
  const items = useSelector(widgetCartItemsSelector)
  const isPaying = useSelector(widgetCartPayingSelector)
  const isActive = items.length > 0 && !isPaying && !items.some((item) => item.holdStatus === CART_HOLD_STATUSES.booked)
  const isHeld = items.some((item) => item.holdStatus === CART_HOLD_STATUSES.held)
  const isTicking = isActive && isHeld && !!hold?.expiresAt
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    if (!isTicking) return undefined
    setNow(Date.now())
    const timer = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(timer)
  }, [isTicking, hold?.expiresAt])

  const remainingMs = isTicking ? Math.max(0, hold.expiresAt - now) : 0
  const isExpired = isActive && remainingMs === 0
  const isReservationOver = (isTicking && remainingMs === 0) || (isActive && items.some((item) => !item.holdStatus || item.holdStatus === CART_HOLD_STATUSES.expired))
  return {
    isActive,
    remainingMs,
    isExpired,
    isReservationOver,
    expiresAt: hold?.expiresAt ?? null,
    extensionsLeft: hold?.extensionsLeft ?? 0,
    shouldAsk: isTicking && !isExpired && remainingMs <= CART_HOLD_PROMPT_SECONDS * 1000
  }
}

export default useCartHold
